import React, { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import { useDuoHeaderItem } from '@/components/Navigation/DuoHeader';
import { duoMenuItems } from '@/components/Navigation/duoMenuItems';

// One presentation for cloud and native chats. The supplied controller owns
// data and effects; this view never loads or mutates a cloud conversation.
export default function ChatDropdownView({
  TriggerComponent,
  children,
  triggerAsChild = false,
  noRoot,
  withNewChat,
  withFindInChat = false,
  withSelectMultipleChats = false,
  withChatDetails,
  open,
  onOpenChange,
  isLongPress,
  side,
  controller
}) {
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    isXS
  } = useResponsiveness();
  const [isCollectionsSubOpen, setIsCollectionsSubOpen] = useState(false);
  const [isExportSubOpen, setIsExportSubOpen] = useState(false);
  useEffect(() => {
    if (!open) { setIsExportSubOpen(false); setIsCollectionsSubOpen(false); }
  }, [open]);
  const {
    chat,
    isTemporary,
    canShare,
    isPinned,
    isArchiving,
    isDeleting,
    isArchived,
    isGhost,
    handleArchiveConfirm,
    handleDeleteConfirm,
    handleShare,
    handleTogglePinChat,
    handleRename,
    handleExportChat,
    handleSaveTemporaryConfirm,
    handleOpenTemporarySettings,
    isSelectingMultipleChats,
    handleStartSelecting,
    collections,
    inCollectionId,
    handleCreateNewCollection,
    handleFindInChat,
    canOpenDeck,
    isCreatingDeck,
    handleOpenDeck,
    canManageWorkTasks,
    handleManageWorkTasks,
    handleReport,
    handleAddToCollection,
    handleNewChat,
    handleDetails,
    handleChanges,
    disabled = false,
    deleteDisabled = false,
    renameDisabled = false,
    capabilities = {}
  } = controller;
  const canPin = capabilities.pin !== false,
    canRename = capabilities.rename !== false;
  const canExport = capabilities.export !== false,
    canCollections = capabilities.collections !== false;
  const canArchive = capabilities.archive !== false,
    canReport = capabilities.report !== false;
  const handleDropdownOpenChange = useCallback(value => {
    if (!value) {
      setIsExportSubOpen(false);
      setIsCollectionsSubOpen(false);
    }
    onOpenChange?.(!disabled && value);
  }, [onOpenChange, disabled]);
  const content = <>
      {(TriggerComponent || children) && <DropdownMenu.Trigger asChild={triggerAsChild} action={isLongPress ? 'longPress' : undefined} disabled={disabled}>
          {TriggerComponent ? <TriggerComponent /> : children}
        </DropdownMenu.Trigger>}

      <DropdownMenu.Content className={Platform.OS === 'web' ? "DropdownMenuContent" : undefined} side={side} align={isXS ? 'center' : 'end'} sideOffset={isXS ? 4 : 5}>
        {Platform.OS === 'web' ?
      // Web implementation
      <>
            {isXS && isExportSubOpen ?
        // Mobile inline export sub-view
        <>
                <div key="export-back" className="DropdownMenuItem" onClick={() => setIsExportSubOpen(false)}>
                  <div className="DropdownMenuItemIcon">
                    <Icon name="chevron-left" size={12} color={colors.text_color_primary} />
                  </div>
                  <span className="DropdownMenuItemTitle">
                    {t('dropdowns.back')}
                  </span>
                </div>
                <DropdownMenuItemDivider key="export-back-divider" />
                <DropdownMenuItem key="export-pdf" onSelect={() => handleExportChat('pdf')} icon="file-pdf" title={t('dropdowns.exportAsPdf')} isDisabled={disabled} />
                <DropdownMenuItem key="export-markdown" onSelect={() => handleExportChat('markdown')} icon="file-lines" title={t('dropdowns.exportAsMarkdown')} isDisabled={disabled} />
                <DropdownMenuItem key="export-text" onSelect={() => handleExportChat('text')} icon="file" title={t('dropdowns.exportAsText')} isDisabled={disabled} />
              </> : isXS && isCollectionsSubOpen ?
        // Mobile inline collections sub-view
        <>
                <div key="collection-back" className="DropdownMenuItem" onClick={() => setIsCollectionsSubOpen(false)}>
                  <div className="DropdownMenuItemIcon">
                    <Icon name="chevron-left" size={12} color={colors.text_color_primary} />
                  </div>
                  <span className="DropdownMenuItemTitle">
                    {t('dropdowns.back')}
                  </span>
                </div>
                <DropdownMenuItemDivider key="collection-back-divider" />
                {Array.isArray(collections) && collections?.filter(collection => collection.id !== inCollectionId).map(collection => <DropdownMenuItem key={`collection-${collection.id}`} onSelect={() => handleAddToCollection(collection.id)} icon="folder" title={collection.title} isDisabled={disabled} />)}
                {(Array.isArray(collections) && collections?.length === 0 || inCollectionId && collections.length === 1) && <>
                    <DropdownMenuItem key="new-collection" icon="folder-plus" title={t('dropdowns.newCollection')} onSelect={handleCreateNewCollection} isDisabled={disabled} />
                    <DropdownMenuItemDivider key="collection-divider" />
                    <DropdownMenuItem key="no-collections" icon="folder" title={t('dropdowns.noCollections')} disabled isDisabled={disabled} />
                  </>}
              </> :
        // Main menu (always on desktop, default on mobile)
        <>
                {(chat?.title || isTemporary) && <>
                    <DropdownMenuLabel key="chat-title" text={isTemporary ? t('home.temporaryChat') : chat.title} />
                    <DropdownMenuItemDivider key="chat-title-divider" />
                  </>}
                {isTemporary && <>
                    <DropdownMenuItem key="save-temporary" onSelect={handleSaveTemporaryConfirm} icon="bookmark" title={t('dropdowns.saveThisChat')} isDisabled={disabled} />
                    <DropdownMenuItem key="temporary-settings" onSelect={handleOpenTemporarySettings} icon="sliders" title={t('dropdowns.temporaryChatSettings')} isDisabled={disabled} />
                    <DropdownMenuItemDivider key="temporary-divider" />
                  </>}
                {withNewChat && <DropdownMenuItem key="new-chat" onSelect={handleNewChat} icon="pen-to-square" title={t('dropdowns.newChat')} isDisabled={disabled} />}
                {canOpenDeck && <DropdownMenuItem key="open-deck" onSelect={handleOpenDeck} icon="table-columns" title={t(isCreatingDeck ? 'dropdowns.creatingDeck' : 'dropdowns.openInNewDeck')} subtitle={t('dropdowns.openInNewDeckDescription')} isDisabled={disabled || isCreatingDeck} isLoading={isCreatingDeck} />}
                {withSelectMultipleChats && !inCollectionId && !isTemporary && <DropdownMenuItem key="select-multiple" onSelect={handleStartSelecting} icon="square-check" title={isSelectingMultipleChats ? t('dropdowns.doneSelecting') : t('dropdowns.select')} isDisabled={disabled} />}
                {!isGhost && !isTemporary && <>{canPin && <DropdownMenuItem key="pin" onSelect={handleTogglePinChat} icon="star" title={isPinned ? t('dropdowns.unpin') : t('dropdowns.pin')} isDisabled={disabled} />}</>}
                {withFindInChat && !isTemporary && <DropdownMenuItem key="find-in-chat" onSelect={handleFindInChat} icon="magnifying-glass" title={t('nav.routes.chat.findInChat')} isDisabled={disabled} />}
                {!isTemporary && <>{canRename && <DropdownMenuItem key="rename" onSelect={handleRename} icon="pencil" title={t('dropdowns.rename')} isDisabled={disabled || renameDisabled} />}{handleChanges && <DropdownMenuItem key="changes" onSelect={handleChanges} icon="code-compare" title="Changes" isDisabled={disabled} />}</>}
                {canManageWorkTasks && <DropdownMenuItem key="work-tasks" onSelect={handleManageWorkTasks} icon="calendar-check" title={t('tasks.manageWorkChatTasks')} isDisabled={disabled} />}
                {withChatDetails && <DropdownMenu.Item key="chat-details" className="DropdownMenuItem" onSelect={handleDetails} disabled={disabled}>
                    <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                      <Icon name="table-columns" size={14} color={colors.text_color_primary} />
                    </DropdownMenu.ItemIcon>
                    <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                      {t('dropdowns.chatDetails')}
                    </DropdownMenu.ItemTitle>
                    <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                      <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                    </DropdownMenu.ItemIcon>
                  </DropdownMenu.Item>}
                {!isGhost && !isTemporary && <>
                    {canShare && <DropdownMenuItem key="share" onSelect={handleShare} icon="share" title={t('dropdowns.share')} isDisabled={disabled} />}
                    {canExport && (isXS ? <div key="export-trigger" className="DropdownMenuItem" onClick={() => setIsExportSubOpen(true)}>
                        <div className="DropdownMenuItemIcon">
                          <Icon name="file-export" size={14} color={colors.text_color_primary} />
                        </div>
                        <span className="DropdownMenuItemTitle">
                          {t('dropdowns.exportChat')}
                        </span>
                        <div className="DropdownMenuItemIcon RightSlot">
                          <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                        </div>
                      </div> : <DropdownMenu.Sub open={isExportSubOpen} onOpenChange={setIsExportSubOpen}>
                        <DropdownMenu.SubTrigger className="DropdownMenuItem" key="export-trigger">
                          <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                            <Icon name="file-export" size={14} color={colors.text_color_primary} />
                          </DropdownMenu.ItemIcon>
                          <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                            {t('dropdowns.exportChat')}
                          </DropdownMenu.ItemTitle>
                          <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                            <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                          </DropdownMenu.ItemIcon>
                        </DropdownMenu.SubTrigger>

                        <DropdownMenu.SubContent className="DropdownMenuSubContent" side="right" align="start" sideOffset={4} avoidCollisions={true} collisionPadding={16}>
                          <DropdownMenuItem key="export-pdf" onSelect={() => handleExportChat('pdf')} icon="file-pdf" title={t('dropdowns.exportAsPdf')} />
                          <DropdownMenuItem key="export-markdown" onSelect={() => handleExportChat('markdown')} icon="file-lines" title={t('dropdowns.exportAsMarkdown')} />
                          <DropdownMenuItem key="export-text" onSelect={() => handleExportChat('text')} icon="file" title={t('dropdowns.exportAsText')} />
                        </DropdownMenu.SubContent>
                      </DropdownMenu.Sub>)}
                    {canCollections && (isXS ? <div key="collection-trigger" className="DropdownMenuItem" onClick={() => setIsCollectionsSubOpen(true)}>
                        <div className="DropdownMenuItemIcon">
                          <Icon name="folder-plus" size={14} color={colors.text_color_primary} />
                        </div>
                        <span className="DropdownMenuItemTitle">
                          {inCollectionId ? t('dropdowns.moveToCollection') : t('dropdowns.addToCollection')}
                        </span>
                        <div className="DropdownMenuItemIcon RightSlot">
                          <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                        </div>
                      </div> : <DropdownMenu.Sub open={isCollectionsSubOpen} onOpenChange={setIsCollectionsSubOpen}>
                        <DropdownMenu.SubTrigger className="DropdownMenuItem" key="collection-trigger">
                          <DropdownMenu.ItemIcon className="DropdownMenuItemIcon">
                            <Icon name="folder-plus" size={14} color={colors.text_color_primary} />
                          </DropdownMenu.ItemIcon>
                          <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                            {inCollectionId ? t('dropdowns.moveToCollection') : t('dropdowns.addToCollection')}
                          </DropdownMenu.ItemTitle>
                          <DropdownMenu.ItemIcon className="DropdownMenuItemIcon RightSlot">
                            <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
                          </DropdownMenu.ItemIcon>
                        </DropdownMenu.SubTrigger>

                        <DropdownMenu.SubContent className="DropdownMenuSubContent" side="right" align="start" sideOffset={4} avoidCollisions={true} collisionPadding={16}>
                          {Array.isArray(collections) && collections?.filter(collection => collection.id !== inCollectionId).map(collection => <DropdownMenuItem key={`collection-${collection.id}`} onSelect={() => handleAddToCollection(collection.id)} icon="folder" title={collection.title} />)}
                          {(Array.isArray(collections) && collections?.length === 0 || inCollectionId && collections.length === 1) && <>
                              <DropdownMenuItem key="new-collection" icon="folder-plus" title={t('dropdowns.newCollection')} onSelect={handleCreateNewCollection} />
                              <DropdownMenuItemDivider key="collection-divider" />
                              <DropdownMenuItem key="no-collections" icon="folder" title={t('dropdowns.noCollections')} disabled />
                            </>}
                        </DropdownMenu.SubContent>
                      </DropdownMenu.Sub>)}

                    {inCollectionId && !isTemporary && <>{canCollections && <DropdownMenuItem key="remove-from-collection" onSelect={() => handleAddToCollection(null)} icon="folder-minus" title={t('dropdowns.removeFrom').replace('%NAME%', collections.find(c => c.id === inCollectionId)?.title || t('dropdowns.collection'))} isDisabled={disabled} />}</>}

                    {!isTemporary && <>{canArchive && <DropdownMenuItem key="archive" onSelect={handleArchiveConfirm} icon="box-archive" title={isArchived ? t('dropdowns.unarchive') : t('dropdowns.archive')} isLoading={isArchiving} isDisabled={disabled} />}</>}
                  </>}

                <>{canReport && <DropdownMenuItem key="report" onSelect={handleReport} icon="flag" title={t('dropdowns.report')} isDisabled={disabled} />}</>

                <DropdownMenuItem key="delete" onSelect={handleDeleteConfirm} icon="trash" title={t('common.delete')} isDanger={true} isLoading={isDeleting} isDisabled={disabled || deleteDisabled} />
              </>}
          </> :
      // Native implementation
      <>
            {(chat?.title || isTemporary) && <>
                <DropdownMenu.Item key="chat-title" disabled>
                  <DropdownMenu.ItemTitle numberOfLines={1}>
                    {isTemporary ? t('home.temporaryChat') : chat.title}
                  </DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
              </>}
            {isTemporary && <>
                <DropdownMenu.Item key="save-temporary" onSelect={handleSaveTemporaryConfirm} disabled={disabled}>
                  <DropdownMenu.ItemIcon ios={{
              name: "bookmark",
              color: colors.text_color_primary
            }} />
                  <DropdownMenu.ItemTitle>{t('dropdowns.saveThisChat')}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Item key="temporary-settings" onSelect={handleOpenTemporarySettings} disabled={disabled}>
                  <DropdownMenu.ItemIcon ios={{
              name: "slider.horizontal.3",
              color: colors.text_color_primary
            }} />
                  <DropdownMenu.ItemTitle>{t('dropdowns.temporaryChatSettings')}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
              </>}
            {withNewChat && <DropdownMenu.Item key="new-chat" onSelect={handleNewChat} disabled={disabled}>
                <DropdownMenu.ItemIcon ios={{
            name: "square.and.pencil",
            color: colors.text_color_primary
          }} />
                <DropdownMenu.ItemTitle>{t('dropdowns.newChat')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>}
            {canOpenDeck && <DropdownMenu.Item key="open-deck" disabled={disabled || isCreatingDeck} onSelect={handleOpenDeck}>
                <DropdownMenu.ItemIcon ios={{
            name: 'sidebar.right',
            color: colors.text_color_primary
          }} />
                <DropdownMenu.ItemTitle>{t(isCreatingDeck ? 'dropdowns.creatingDeck' : 'dropdowns.openInNewDeck')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>}
            {withSelectMultipleChats && !inCollectionId && !isTemporary && <DropdownMenu.Item key="select-multiple" onSelect={handleStartSelecting} disabled={disabled}>
                <DropdownMenu.ItemIcon ios={{
            name: "checkmark.circle",
            color: colors.text_color_primary
          }} />
                <DropdownMenu.ItemTitle>
                  {isSelectingMultipleChats ? t('dropdowns.doneSelecting') : t('dropdowns.select')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>}
            {!isGhost && !isTemporary && <>{canPin && <DropdownMenu.Item key="pin" onSelect={handleTogglePinChat} disabled={disabled}>
                <DropdownMenu.ItemIcon ios={{
              name: "star",
              color: colors.text_color_primary
            }} />
                <DropdownMenu.ItemTitle>{isPinned ? t('dropdowns.unpin') : t('dropdowns.pin')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>}</>}
            {withFindInChat && !isTemporary && <DropdownMenu.Item key="find-in-chat" onSelect={handleFindInChat} disabled={disabled}>
                <DropdownMenu.ItemIcon ios={{
            name: "magnifyingglass",
            color: colors.text_color_primary
          }} />
                <DropdownMenu.ItemTitle>{t('nav.routes.chat.findInChat')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>}
            {!isTemporary && <>{canRename && <DropdownMenu.Item key="rename" onSelect={handleRename} disabled={disabled || renameDisabled}>
                <DropdownMenu.ItemIcon ios={{
              name: "pencil",
              color: colors.text_color_primary
            }} />
                <DropdownMenu.ItemTitle>{t('dropdowns.rename')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>}{handleChanges && <DropdownMenu.Item key="changes" onSelect={handleChanges} disabled={disabled}><DropdownMenu.ItemIcon ios={{
              name: "arrow.triangle.branch",
              color: colors.text_color_primary
            }} /><DropdownMenu.ItemTitle>Changes</DropdownMenu.ItemTitle></DropdownMenu.Item>}</>}
            {canManageWorkTasks && <DropdownMenu.Item key="work-tasks" onSelect={handleManageWorkTasks} disabled={disabled}>
                <DropdownMenu.ItemIcon ios={{
            name: 'calendar',
            color: colors.text_color_primary
          }} />
                <DropdownMenu.ItemTitle>{t('tasks.manageWorkChatTasks')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>}
            {withChatDetails && !isTemporary && <DropdownMenu.Item key="chat-details" onSelect={handleDetails} disabled={disabled}>
                <DropdownMenu.ItemIcon ios={{
            name: "sidebar.right",
            color: colors.text_color_primary
          }} />
                <DropdownMenu.ItemTitle>{t('dropdowns.chatDetails')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>}
            {!isGhost && !isTemporary && <>
                {canShare && <DropdownMenu.Item key="share" onSelect={handleShare} disabled={disabled}>
                    <DropdownMenu.ItemIcon ios={{
              name: "square.and.arrow.up",
              color: colors.text_color_primary
            }} />
                    <DropdownMenu.ItemTitle>{t('dropdowns.share')}</DropdownMenu.ItemTitle>
                  </DropdownMenu.Item>}

                <>{canExport && <DropdownMenu.Sub open={isExportSubOpen} onOpenChange={setIsExportSubOpen}>
                  <DropdownMenu.SubTrigger key="export-trigger" disabled={disabled}>
                    <DropdownMenu.ItemIcon ios={{
                  name: "square.and.arrow.down",
                  color: colors.text_color_primary
                }} />
                    <DropdownMenu.ItemTitle>{t('dropdowns.exportChat')}</DropdownMenu.ItemTitle>
                  </DropdownMenu.SubTrigger>

                  <DropdownMenu.SubContent side={isXS ? 'bottom' : 'right'} align={isXS ? 'center' : 'start'} sideOffset={isXS ? 6 : 4}>
                    <DropdownMenu.Item key="export-pdf" onSelect={() => handleExportChat('pdf')} disabled={disabled}>
                      <DropdownMenu.ItemIcon ios={{
                    name: "doc.fill",
                    color: colors.text_color_primary
                  }} />
                      <DropdownMenu.ItemTitle>{t('dropdowns.exportAsPdf')}</DropdownMenu.ItemTitle>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item key="export-markdown" onSelect={() => handleExportChat('markdown')} disabled={disabled}>
                      <DropdownMenu.ItemIcon ios={{
                    name: "doc.richtext",
                    color: colors.text_color_primary
                  }} />
                      <DropdownMenu.ItemTitle>{t('dropdowns.exportAsMarkdown')}</DropdownMenu.ItemTitle>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item key="export-text" onSelect={() => handleExportChat('text')} disabled={disabled}>
                      <DropdownMenu.ItemIcon ios={{
                    name: "doc.text",
                    color: colors.text_color_primary
                  }} />
                      <DropdownMenu.ItemTitle>{t('dropdowns.exportAsText')}</DropdownMenu.ItemTitle>
                    </DropdownMenu.Item>
                  </DropdownMenu.SubContent>
                </DropdownMenu.Sub>}</>

                <>{canCollections && <DropdownMenu.Sub open={isCollectionsSubOpen} onOpenChange={setIsCollectionsSubOpen}>
                  <DropdownMenu.SubTrigger key="collection-trigger" disabled={disabled}>
                    <DropdownMenu.ItemIcon ios={{
                  name: "folder.badge.plus",
                  color: colors.text_color_primary
                }} />
                    <DropdownMenu.ItemTitle>
                      {inCollectionId ? t('dropdowns.moveToCollection') : t('dropdowns.addToCollection')}
                    </DropdownMenu.ItemTitle>
                  </DropdownMenu.SubTrigger>

                  <DropdownMenu.SubContent side={isXS ? 'bottom' : 'right'} align={isXS ? 'center' : 'start'} sideOffset={isXS ? 6 : 4}>
                    {isXS && <>
                        <DropdownMenu.Item key="collection-back" onSelect={() => setIsCollectionsSubOpen(false)} disabled={disabled}>
                          <DropdownMenu.ItemIcon ios={{
                      name: "chevron.backward",
                      color: colors.text_color_primary
                    }} />
                          <DropdownMenu.ItemTitle>{t('dropdowns.back')}</DropdownMenu.ItemTitle>
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator />
                      </>}
                    {Array.isArray(collections) && collections?.filter(collection => collection.id !== inCollectionId).map(collection => <DropdownMenu.Item key={`collection-${collection.id}`} onSelect={() => handleAddToCollection(collection.id)} disabled={disabled}>
                        <DropdownMenu.ItemIcon ios={{
                    name: "folder",
                    color: colors.text_color_primary
                  }} />
                        <DropdownMenu.ItemTitle>{collection.title}</DropdownMenu.ItemTitle>
                      </DropdownMenu.Item>)}
                    {(Array.isArray(collections) && collections?.length === 0 || inCollectionId && collections.length === 1) && <>
                        <DropdownMenu.Item key="new-collection" onSelect={handleCreateNewCollection} disabled={disabled}>
                          <DropdownMenu.ItemIcon ios={{
                      name: "folder.badge.plus",
                      color: colors.text_color_primary
                    }} />
                          <DropdownMenu.ItemTitle>{t('dropdowns.newCollection')}</DropdownMenu.ItemTitle>
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator key="collection-divider" />
                        <DropdownMenu.Item key="no-collections" disabled>
                          <DropdownMenu.ItemIcon ios={{
                      name: "folder",
                      color: colors.text_color_secondary
                    }} />
                          <DropdownMenu.ItemTitle>{t('dropdowns.noCollections')}</DropdownMenu.ItemTitle>
                        </DropdownMenu.Item>
                      </>}
                  </DropdownMenu.SubContent>
                </DropdownMenu.Sub>}</>

                {inCollectionId && <>{canCollections && <DropdownMenu.Item key="remove-from-collection" onSelect={() => handleAddToCollection(null)} disabled={disabled}>
                    <DropdownMenu.ItemIcon ios={{
                name: "folder.badge.minus",
                color: colors.text_color_primary
              }} />
                    <DropdownMenu.ItemTitle>
                      {t('dropdowns.removeFrom').replace('%NAME%', collections.find(c => c.id === inCollectionId)?.title || t('dropdowns.collection'))}
                    </DropdownMenu.ItemTitle>
                  </DropdownMenu.Item>}</>}

                <>{canArchive && <DropdownMenu.Item key="archive" onSelect={handleArchiveConfirm} disabled={disabled}>
                  <DropdownMenu.ItemIcon ios={{
                name: "archivebox",
                color: colors.text_color_primary
              }} />
                  <DropdownMenu.ItemTitle>
                    {isArchived ? t('dropdowns.unarchive') : t('dropdowns.archive')}
                  </DropdownMenu.ItemTitle>
                </DropdownMenu.Item>}</>
              </>}
            {!isGhost && <>
                <>{canReport && <DropdownMenu.Item key="report" onSelect={handleReport} disabled={disabled}>
                  <DropdownMenu.ItemIcon ios={{
                name: "flag",
                color: colors.text_color_primary
              }} />
                  <DropdownMenu.ItemTitle>{t('dropdowns.report')}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>}</>

                <DropdownMenu.Item key="delete" destructive onSelect={handleDeleteConfirm} disabled={disabled || deleteDisabled}>
                  <DropdownMenu.ItemIcon ios={{
              name: "trash"
            }} />
                  <DropdownMenu.ItemTitle>
                    {t('common.delete')}
                  </DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
              </>}
          </>}
      </DropdownMenu.Content>
    </>;
  const inDuoBar = useDuoHeaderItem({ title: t('nav.routes.chat.moreOptions'), symbol: 'ellipsis',
    disabled, children: Platform.OS === 'ios' ? duoMenuItems(content, DropdownMenu) : [] });
  if (inDuoBar) return null;
  if (noRoot) {
    return content;
  }
  return <DropdownMenu.Root {...open !== undefined ? {
    open
  } : {}} onOpenChange={handleDropdownOpenChange}>
      {content}
    </DropdownMenu.Root>;
}
